import React, { useEffect, useState, useRef } from "react";
// Import search params hook from React Router to read token parameters from URL
import { useSearchParams } from "react-router-dom";
// Import loadStripe from the Stripe JS SDK library
import { loadStripe } from "@stripe/stripe-js";
// Import Elements wrappers and hooks from Stripe React bindings
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
// Import Convex action hook
import { useAction } from "convex/react";
// Import backend API reference
import { api } from "../../convex/_generated/api";

// Initialize Stripe publishable key once
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "");

/**
 * MobileStripeCheckoutForm (Child Component)
 * Mounts the payment form with Stripe's PaymentElement.
 * This element handles Apple Pay, Google Pay, and credit cards automatically based on availability.
 */
const MobileStripeCheckoutForm: React.FC<{
  onSuccess?: () => void;
}> = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    // Verify element forms are filled and submit values to Stripe elements pipeline
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message || "Please check your payment fields.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Confirm the Stripe payment with elements context
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing?success=true`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setErrorMessage(result.error.message || "An unexpected checkout error occurred.");
        setIsSubmitting(false);
      } else {
        setIsSubmitting(false);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Confirmation failed.");
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="mobile-checkout-form" style={{ marginTop: "1rem" }}>
      <PaymentElement />

      {errorMessage && (
        <div role="alert" style={{ color: "#FF0055", marginTop: "1rem", fontSize: "0.9rem" }}>
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isSubmitting}
        className="btn-primary"
        style={{
          width: "100%",
          padding: "0.75rem",
          marginTop: "1.5rem",
          fontSize: "1rem",
          fontWeight: "600",
        }}
      >
        {isSubmitting ? "Processing..." : "Complete Mobile Payment"}
      </button>
    </form>
  );
};

/**
 * MobilePayPage (Parent Page Component)
 * Handles mobile payment handoff redirection.
 * Extracts "t" query parameter, resolves the payment token, and displays Stripe checkout elements.
 */
export const MobilePayPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Extract token from query params: /mobile-pay?t=token_hash
  const payToken = searchParams.get("t");

  const resolvePayToken = useAction(api.stripe.resolvePayToken);

  const [clientSecret, setClientSecret] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Prevent multiple executions in StrictMode (Security constraint: single-use tokens)
  const resolvedRef = useRef<string | null>(null);

  useEffect(() => {
    // Show direct error if the token is completely missing from query string
    if (!payToken) {
      setError("No payment token was provided. Please scan the QR code from your desktop dashboard again.");
      return;
    }

    if (resolvedRef.current === payToken) {
      return;
    }
    resolvedRef.current = payToken;

    const performResolution = async () => {
      setLoading(true);
      setError(null);
      setClientSecret("");

      try {
        const result = await resolvePayToken({ payToken });
        setClientSecret(result.clientSecret);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "The payment token is invalid, expired, or has already been used."
        );
      } finally {
        setLoading(false);
      }
    };

    performResolution();
  }, [payToken, resolvePayToken]);

  // Loading skeleton matching the neon styling
  if (loading) {
    return (
      <div
        className="mobile-pay-container"
        style={{
          padding: "3rem 1.5rem",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--color-bg-primary)",
        }}
        data-testid="mobile-pay-loading"
      >
        <div className="loading-spinner" style={{ borderTopColor: "#00D4FF", width: "48px", height: "48px", border: "4px solid rgba(255,255,255,0.1)", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
        <p style={{ marginTop: "1rem", color: "var(--color-text-secondary)" }}>
          Resolving secure payment session...
        </p>
      </div>
    );
  }

  // Error views displaying invalid / expired token messages
  if (error) {
    return (
      <div
        className="mobile-pay-container"
        style={{
          padding: "3rem 1.5rem",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--color-bg-primary)",
          textAlign: "center",
        }}
        data-testid="mobile-pay-error"
      >
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>
        <h2 style={{ color: "#FF0055", fontSize: "1.5rem", fontWeight: "700", marginBottom: "1rem" }}>
          Unable to Load Checkout
        </h2>
        <p style={{ color: "var(--color-text-secondary)", maxWidth: "400px", lineHeight: "1.5" }}>
          {error}
        </p>
      </div>
    );
  }

  // Success view when payment completes successfully
  if (success) {
    return (
      <div
        className="mobile-pay-container"
        style={{
          padding: "3rem 1.5rem",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--color-bg-primary)",
          textAlign: "center",
        }}
        data-testid="mobile-pay-success"
      >
        <div style={{ fontSize: "3.5rem", color: "#00D4FF", marginBottom: "1rem" }}>✓</div>
        <h2 style={{ color: "#FFFFFF", fontSize: "1.5rem", fontWeight: "700", marginBottom: "1rem" }}>
          Payment Completed!
        </h2>
        <p style={{ color: "var(--color-text-secondary)", maxWidth: "400px", lineHeight: "1.5" }}>
          Your payment has been authorized. You can close this browser page now. Your desktop app will automatically update to reflect your upgrade.
        </p>
      </div>
    );
  }

  // Fallback if client secret failed to resolve or token is empty
  if (!clientSecret) {
    return (
      <div
        style={{
          padding: "3rem 1.5rem",
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--color-bg-primary)",
          textAlign: "center",
        }}
        data-testid="mobile-pay-missing"
      >
        <p style={{ color: "var(--color-text-secondary)" }}>
          No active payment session. Please verify your handshake URL.
        </p>
      </div>
    );
  }

  // Customize Stripe appearance to match the neon application UI
  const stripeOptions = {
    clientSecret,
    appearance: {
      theme: "night" as const,
      variables: {
        colorPrimary: "#00D4FF",
        colorBackground: "#161D30",
      },
    },
  };

  return (
    <div
      className="mobile-pay-container"
      style={{
        padding: "3rem 1.5rem",
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "var(--color-bg-primary)",
        color: "var(--color-text-primary)",
      }}
      data-testid="mobile-pay-checkout-container"
    >
      <div
        className="mobile-pay-card"
        style={{
          width: "100%",
          maxWidth: "420px",
          backgroundColor: "#161D30",
          borderRadius: "var(--border-radius-md)",
          padding: "2rem",
          boxShadow: "0 0 20px rgba(0, 212, 255, 0.15)",
        }}
      >
        <h2 style={{ color: "#FFFFFF", fontSize: "1.35rem", fontWeight: "700", marginBottom: "0.5rem" }}>
          Complete Purchase
        </h2>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
          Configure your details below. Supports credit card, Apple Pay, and Google Pay.
        </p>

        {/* Elements provider container wrapping Checkout Elements form */}
        <Elements stripe={stripePromise} options={stripeOptions}>
          <MobileStripeCheckoutForm onSuccess={() => setSuccess(true)} />
        </Elements>
      </div>
    </div>
  );
};
